import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Location } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { useLoad, useSubmit } from '../lib/useAsync';
import { formatMoney } from '../lib/format';
import type { Language } from '../i18n';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';

interface FormState {
  id: number | null;
  code: string;
  name: string;
  type: 'WAREHOUSE' | 'STORE';
  can_distribute: boolean;
  min_total: number;
  active: boolean;
  /** Gastos fijos mensuales (texto del campo; vacío = 0). */
  rent: string;
  payroll: string;
  other_fixed: string;
}

const EMPTY: FormState = { id: null, code: '', name: '', type: 'STORE', can_distribute: false, min_total: 150, active: true, rent: '', payroll: '', other_fixed: '' };

/** Vista "Locales" del prototipo: tarjetas por ubicación, alta y edición. */
export function Locations() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show, showError } = useToast();
  const { data, reload } = useLoad(() => api.locations());
  const [form, setForm] = useState<FormState | null>(null);
  const { busy, run } = useSubmit();
  const manage = can('config.manage');
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const money = (n: number) => formatMoney(n, lang);

  const open = (l?: Location) =>
    setForm(l ? { id: l.id, code: l.code, name: l.name, type: l.type, can_distribute: l.can_distribute, min_total: l.min_total, active: l.active, rent: String(l.rent ?? ''), payroll: String(l.payroll ?? ''), other_fixed: String(l.other_fixed ?? '') } : EMPTY);

  const scope = form?.id ? `ubicacion:${form.id}` : 'ubicacion:nueva';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    await run(async () => {
      try {
        const key = requestKey(scope);
        let id = form.id;
        if (id) await api.updateLocation(key, id, { name: form.name, can_distribute: form.can_distribute, min_total: form.min_total, active: form.active });
        else id = (await api.createLocation(key, { code: form.code, name: form.name, type: form.type, can_distribute: form.can_distribute, min_total: form.min_total })).id;
        // Los gastos fijos se guardan aparte (tabla propia, visible solo para Gerencia).
        const costs = { rent: Number(form.rent) || 0, payroll: Number(form.payroll) || 0, other_fixed: Number(form.other_fixed) || 0 };
        const before = data?.find((l) => l.id === form.id);
        if (costs.rent !== (before?.rent ?? 0) || costs.payroll !== (before?.payroll ?? 0) || costs.other_fixed !== (before?.other_fixed ?? 0)) {
          await api.setFixedCosts(requestKey(`${scope}:gastos`), id, costs);
          releaseKey(`${scope}:gastos`);
        }
        releaseKey(scope);
        setForm(null);
        show(t('locations.saved'));
        await reload();
      } catch (err) {
        showError(err);
      }
    });
  };

  const toggle = async (l: Location) => {
    await run(async () => {
      const s = `ubicacion:${l.id}:estado:${l.active ? 'cerrar' : 'abrir'}`;
      try {
        await api.updateLocation(requestKey(s), l.id, { name: l.name, can_distribute: l.can_distribute, min_total: l.min_total, active: !l.active });
        releaseKey(s);
        await reload();
      } catch (err) {
        showError(err);
      }
    });
  };

  return (
    <div className="g2">
      <Panel
        title={t('locations.title')}
        description={t('locations.description')}
        badge={
          manage ? (
            <button type="button" className="btn black" id="addStore" onClick={() => open()}>
              {t('locations.add')}
            </button>
          ) : null
        }
      >
        <div className="storecards" id="storeAdmin">
          {(data ?? []).map((l) => (
            <div className="storecard" key={l.id} data-location={l.code}>
              <div className="topline">
                <div>
                  <h4>{l.name}</h4>
                  <p>
                    {l.code} · {t(`locations.type.${l.type}`)}
                    {l.can_distribute ? ` · ${t('locations.distributes')}` : ''}
                  </p>
                </div>
                <Badge tone={l.active ? 'green' : 'red'}>{l.active ? t('locations.active') : t('locations.closed')}</Badge>
              </div>
              <p className="tiny muted">{t('locations.minTotal', { n: l.min_total })}</p>
              {manage && l.rent != null ? (
                <div className="storestats one" data-role="fixed">
                  <div>
                    <small>{t('locations.stats.fixed')}</small>
                    <b data-stat="fixed">{money((l.rent ?? 0) + (l.payroll ?? 0) + (l.other_fixed ?? 0))}</b>
                  </div>
                </div>
              ) : null}
              {manage ? (
                <div className="actions">
                  <button type="button" className="btn sm" onClick={() => open(l)}>
                    {t('common.edit')}
                  </button>
                  <button type="button" className="btn sm" onClick={() => void toggle(l)} disabled={busy}>
                    {l.active ? t('locations.close') : t('locations.open')}
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </Panel>
      <Panel title={t('locations.whyTitle')} description={t('locations.whyDescription')}>
        <div className="alerts">
          <div className="alert" id="profitWhere">
            <div>
              <b>{t('locations.whereTitle')}</b>
              <small>{t('locations.whereBody')}</small>
            </div>
          </div>
          <div className="alert">
            <div>
              <b>{t('locations.why1Title')}</b>
              <small>{t('locations.why1Body')}</small>
            </div>
          </div>
          <div className="alert">
            <div>
              <b>{t('locations.why2Title')}</b>
              <small>{t('locations.why2Body')}</small>
            </div>
          </div>
          <div className="alert">
            <div>
              <b>{t('locations.why3Title')}</b>
              <small>{t('locations.why3Body')}</small>
            </div>
          </div>
        </div>
      </Panel>

      <Modal open={form !== null} title={form?.id ? t('locations.editTitle') : t('locations.newTitle')} help={t('locations.modalHelp')} onClose={() => setForm(null)}>
        {form ? (
          <form className="formgrid" id="storeForm" onSubmit={(e) => void submit(e)}>
            {!form.id ? (
              <div className="field">
                <label htmlFor="stCode">{t('locations.fields.code')}</label>
                <input id="stCode" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              </div>
            ) : null}
            {!form.id ? (
              <div className="field">
                <label htmlFor="stType">{t('locations.fields.type')}</label>
                <select id="stType" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as FormState['type'] })}>
                  <option value="STORE">{t('locations.type.STORE')}</option>
                  <option value="WAREHOUSE">{t('locations.type.WAREHOUSE')}</option>
                </select>
              </div>
            ) : null}
            <div className="field span2">
              <label htmlFor="stName">{t('locations.fields.name')}</label>
              <input id="stName" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="stMin">{t('locations.fields.minTotal')}</label>
              <input id="stMin" type="number" min={0} value={form.min_total} onChange={(e) => setForm({ ...form, min_total: Number(e.target.value) })} />
            </div>
            <div className="field">
              <label htmlFor="stDistribute">{t('locations.fields.canDistribute')}</label>
              <select id="stDistribute" value={form.can_distribute ? '1' : '0'} onChange={(e) => setForm({ ...form, can_distribute: e.target.value === '1' })}>
                <option value="0">{t('common.no')}</option>
                <option value="1">{t('common.yes')}</option>
              </select>
            </div>
            <h4 className="span2 formsection">{t('locations.fixedTitle')}</h4>
            <div className="field">
              <label htmlFor="stRent">{t('locations.fields.rent')}</label>
              <input id="stRent" type="number" min={0} placeholder="0" value={form.rent} onChange={(e) => setForm({ ...form, rent: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="stPayroll">{t('locations.fields.payroll')}</label>
              <input id="stPayroll" type="number" min={0} placeholder="0" value={form.payroll} onChange={(e) => setForm({ ...form, payroll: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="stOther">{t('locations.fields.otherFixed')}</label>
              <input id="stOther" type="number" min={0} placeholder="0" value={form.other_fixed} onChange={(e) => setForm({ ...form, other_fixed: e.target.value })} />
            </div>
            <div className="field">
              <label>{t('locations.fields.fixedTotal')}</label>
              <div className="calcline" id="stFixedTotal">{money((Number(form.rent) || 0) + (Number(form.payroll) || 0) + (Number(form.other_fixed) || 0))}</div>
            </div>
            {form.id ? (
              <div className="field span2">
                <label htmlFor="stActive">{t('locations.fields.status')}</label>
                <select id="stActive" value={form.active ? '1' : '0'} onChange={(e) => setForm({ ...form, active: e.target.value === '1' })}>
                  <option value="1">{t('locations.statusOpen')}</option>
                  <option value="0">{t('locations.statusClosed')}</option>
                </select>
              </div>
            ) : null}
            <button className="btn black span2" type="submit" disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </form>
        ) : null}
      </Modal>
    </div>
  );
}
